import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import EmptyState from "@/components/base/EmptyState";
import { getChapters, getComicById } from "@/utils/comics";
import { readerPages } from "@/mocks/readerPages";

type Theme = "white" | "sepia" | "dark";
type Width = "narrow" | "medium" | "wide";

const themeClass: Record<Theme, string> = {
  white: "reader-theme-white",
  sepia: "reader-theme-sepia",
  dark: "reader-theme-dark",
};

const widthClass: Record<Width, string> = {
  narrow: "max-w-[680px]",
  medium: "max-w-[820px]",
  wide: "max-w-[1000px]",
};

const themeOptions: { key: Theme; label: string; swatch: string }[] = [
  { key: "white", label: "白色", swatch: "#ffffff" },
  { key: "sepia", label: "米色", swatch: "#f3ead9" },
  { key: "dark", label: "夜间", swatch: "#14161a" },
];

const widthOptions: { key: Width; label: string }[] = [
  { key: "narrow", label: "窄" },
  { key: "medium", label: "中" },
  { key: "wide", label: "宽" },
];

export default function Read() {
  const { comicId, chapterId } = useParams();
  const navigate = useNavigate();
  const comic = getComicById(comicId);
  const chapters = useMemo(() => (comic ? getChapters(comic) : []), [comic]);

  const currentNumber = Number(chapterId) || 1;
  const [theme, setTheme] = useState<Theme>("white");
  const [width, setWidth] = useState<Width>("medium");
  const [fontSize, setFontSize] = useState(16);
  const [progress, setProgress] = useState(0);
  const [showToolbar, setShowToolbar] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const lastScroll = useRef(0);
  const isDark = theme === "dark";

  useEffect(() => {
    setProgress(0);
    window.scrollTo({ top: 0 });
  }, [chapterId, comicId]);

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const height = document.documentElement.scrollHeight - window.innerHeight;
    setProgress(height > 0 ? Math.min(100, (scrollTop / height) * 100) : 0);
    if (scrollTop > lastScroll.current && scrollTop > 160) {
      setShowToolbar(false);
    } else {
      setShowToolbar(true);
    }
    lastScroll.current = scrollTop;
  };

  if (!comic) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="章节不存在"
          desc="该漫画或章节可能已下架，返回首页看看其他精彩韩漫吧。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const hasPrev = currentNumber > 1;
  const hasNext = currentNumber < comic.chapterCount;
  const goPrev = () => hasPrev && navigate(`/read/${comic.id}/${currentNumber - 1}`);
  const goNext = () => hasNext && navigate(`/read/${comic.id}/${currentNumber + 1}`);

  const toolbarText = isDark ? "text-foreground-100" : "text-foreground-900";
  const toolbarBg = isDark
    ? "bg-[#14161a]/95 border-white/10"
    : "bg-background-50/95 border-background-200";

  return (
    <div
      className={`min-h-screen w-full ${themeClass[theme]}`}
      onScroll={handleScroll}
    >
      <Seo
        title={`${comic.title} 第 ${currentNumber} 话 - 免费在线观看`}
        description={`在线阅读《${comic.title}》第 ${currentNumber} 话，${comic.author} 作品，免费韩漫在线阅读最新章节。`}
        keywords={`${comic.title},第${currentNumber}话,${comic.title}在线观看,韩漫免费观看`}
      />

      <div className="fixed left-0 top-0 z-[70] h-1 w-full bg-black/10">
        <div
          className="h-full bg-primary-500 transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      <header
        className={`fixed left-0 top-1 z-[65] w-full border-b backdrop-blur transition-transform duration-300 ${toolbarBg} ${
          showToolbar ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-3 md:px-6">
          <Link
            to={`/detail/${comic.id}`}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border ${
              isDark ? "border-white/15 text-foreground-100" : "border-background-300 text-foreground-700"
            }`}
            aria-label="返回详情页"
          >
            <i className="ri-arrow-left-line text-lg" />
          </Link>
          <div className="min-w-0 flex-1">
            <p className={`line-clamp-1 text-sm font-semibold ${toolbarText}`}>
              {comic.title}
            </p>
            <p className={`text-xs ${isDark ? "text-foreground-400" : "text-foreground-500"}`}>
              第 {currentNumber} 话 / 共 {comic.chapterCount} 话
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            className={`flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm ${
              isDark ? "border-white/15 text-foreground-100" : "border-background-300 text-foreground-700"
            }`}
          >
            <i className="ri-settings-3-line" />
            <span className="hidden sm:inline">设置</span>
          </button>
        </div>

        {settingsOpen && (
          <div className={`border-t ${isDark ? "border-white/10" : "border-background-200"}`}>
            <div className="mx-auto w-full max-w-6xl px-3 py-4 md:px-6">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${isDark ? "text-foreground-300" : "text-foreground-600"}`}>
                    背景
                  </span>
                  <div className="flex gap-2">
                    {themeOptions.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setTheme(t.key)}
                        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
                          theme === t.key
                            ? "border-primary-500 text-primary-600"
                            : isDark
                              ? "border-white/15 text-foreground-300"
                              : "border-background-300 text-foreground-600"
                        }`}
                      >
                        <span
                          className="h-4 w-4 rounded-full border border-black/10"
                          style={{ backgroundColor: t.swatch }}
                        />
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${isDark ? "text-foreground-300" : "text-foreground-600"}`}>
                    阅读宽度
                  </span>
                  <div className="flex gap-2">
                    {widthOptions.map((w) => (
                      <button
                        key={w.key}
                        type="button"
                        onClick={() => setWidth(w.key)}
                        className={`rounded-full border px-3.5 py-1.5 text-xs ${
                          width === w.key
                            ? "border-primary-500 text-primary-600"
                            : isDark
                              ? "border-white/15 text-foreground-300"
                              : "border-background-300 text-foreground-600"
                        }`}
                      >
                        {w.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${isDark ? "text-foreground-300" : "text-foreground-600"}`}>
                    字号
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFontSize((s) => Math.max(12, s - 1))}
                      className={`flex h-7 w-7 items-center justify-center rounded-md border ${
                        isDark ? "border-white/15 text-foreground-200" : "border-background-300 text-foreground-700"
                      }`}
                      aria-label="减小字号"
                    >
                      <i className="ri-subtract-line" />
                    </button>
                    <span className={`w-8 text-center text-xs ${isDark ? "text-foreground-200" : "text-foreground-700"}`}>
                      {fontSize}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFontSize((s) => Math.min(22, s + 1))}
                      className={`flex h-7 w-7 items-center justify-center rounded-md border ${
                        isDark ? "border-white/15 text-foreground-200" : "border-background-300 text-foreground-700"
                      }`}
                      aria-label="增大字号"
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto w-full px-0 pb-24 pt-16">
        <div className={`mx-auto w-full ${widthClass[width]} px-2 md:px-4`}>
          <div className="py-5 text-center">
            <p
              className={`font-semibold ${isDark ? "text-foreground-200" : "text-foreground-800"}`}
              style={{ fontSize: `${fontSize + 4}px` }}
            >
              {comic.title} · 第 {currentNumber} 话
            </p>
            <p className={`mt-1 text-xs ${isDark ? "text-foreground-500" : "text-foreground-500"}`}>
              共 {readerPages.length} 页 · 向下滚动继续阅读
            </p>
          </div>

          <div className="flex flex-col gap-1">
            {readerPages.map((page, i) => (
              <img
                key={`${comic.id}-${currentNumber}-${i}`}
                src={page}
                alt={`${comic.title} 第 ${currentNumber} 话 第 ${i + 1} 页`}
                title={`${comic.title} 第 ${currentNumber} 话 第 ${i + 1} 页`}
                className="w-full rounded-sm bg-background-100"
              />
            ))}
          </div>

          <div className="mt-8 rounded-lg border border-black/10 bg-black/5 p-5 text-center">
            <p className={`text-sm ${isDark ? "text-foreground-300" : "text-foreground-700"}`}>
              本章已读完，继续追更吧
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                disabled={!hasPrev}
                onClick={goPrev}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-line" />
                上一话
              </button>
              <Link
                to={`/detail/${comic.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
              >
                <i className="ri-list-check-2" />
                章节目录
              </Link>
              <button
                type="button"
                disabled={!hasNext}
                onClick={goNext}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                下一话
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </main>

      <div
        className={`fixed bottom-0 left-0 z-[65] w-full border-t backdrop-blur transition-transform duration-300 ${toolbarBg} ${
          showToolbar ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between gap-3 px-3 md:px-6">
          <button
            type="button"
            disabled={!hasPrev}
            onClick={goPrev}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm ${
              isDark ? "text-foreground-200" : "text-foreground-700"
            } disabled:opacity-40`}
          >
            <i className="ri-arrow-left-s-line text-lg" />
            上一话
          </button>

          <select
            value={currentNumber}
            onChange={(e) => navigate(`/read/${comic.id}/${e.target.value}`)}
            aria-label="选择章节"
            className={`rounded-md border px-3 py-2 text-sm focus:outline-none ${
              isDark
                ? "border-white/15 bg-[#1c1f24] text-foreground-100"
                : "border-background-300 bg-background-50 text-foreground-700"
            }`}
          >
            {chapters.map((ch) => (
              <option key={ch.id} value={ch.id}>
                {ch.title}
              </option>
            ))}
          </select>

          <button
            type="button"
            disabled={!hasNext}
            onClick={goNext}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 disabled:opacity-40"
          >
            下一话
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}